import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Affichage des dates — lot T3 du chantier fuseaux horaires.
 *
 * 🔴 DEUX FAMILLES DE DATES, ET DEUX FONCTIONS QUI RENDENT LA MÊME FORME AVEC
 * DES SÉMANTIQUES OPPOSÉES. C'est la ligne qu'un relecteur pressé confondra,
 * alors elle est écrite au-dessus des deux :
 *
 *   · `formaterHorodatageEnJour(iso)` reçoit un **INSTANT** et le CONVERTIT
 *     dans le fuseau du lecteur avant d'en garder le jour. Un événement à
 *     **23:30 UTC le 11 août est déjà le 12 à Jérusalem** — et il DOIT
 *     s'afficher 12.
 *   · `formaterDateSaisie(chaine)` reçoit un **JOUR CIVIL**, que quelqu'un a
 *     tapé dans un `<input type="date">`. **Il ne bouge jamais.** Le 11 août
 *     est le 11 août à Paris, à Netanya, à New York et à Honolulu.
 *
 * Les deux rendent `"11/08/2026"`. **Seul le nom porte la différence.**
 * Confondre la seconde avec la première décale le jour d'une commande pour la
 * moitié de la planète — c'est le défaut que le lot T2 avait déjà rencontré
 * DEUX FOIS, sous deux formes opposées (cf. ROADMAP, notes de méthode).
 *
 * ⚠️ CE MODULE EXPOSE UN **HOOK**, PAS DES FONCTIONS LIBRES, ET C'EST VOULU.
 * Le format suit la langue choisie DANS L'APPLICATION — pas celle du système
 * du lecteur : l'utilisateur a fait un choix explicite dans l'interface, ses
 * dates doivent le suivre. Lire ce choix veut dire lire `LanguageContext`,
 * donc `useFormatageDates()` **n'est appelable que depuis un composant**.
 * 🔴 SI UN APPEL HORS COMPOSANT DEVIENT NÉCESSAIRE UN JOUR, LA RÉPONSE N'EST
 * **PAS** UNE VARIABLE DE MODULE MISE À JOUR PAR LE `LanguageProvider` : un
 * état global mutable et invisible est pire qu'une contrainte explicite. La
 * réponse est de passer la locale en argument aux fonctions pures ci-dessous.
 *
 * ⚠️ LE FUSEAU, LUI, VIENT DU NAVIGATEUR — c'est-à-dire qu'on n'en parle pas.
 * Aucune option `timeZone` n'est passée nulle part : `toLocaleString()` sans
 * option utilise le fuseau du système, qui est celui du lecteur. Le
 * `timeZone: 'Europe/Paris'` recopié sur 14 sites servait exactement l'heure
 * de Paris au monde entier ; il a disparu de tout le projet à ce lot.
 */

/** `fr` | `en` | `he` → l'étiquette BCP 47 que comprend `Intl`. */
const LOCALES = { fr: 'fr-FR', en: 'en-US', he: 'he-IL' };

const localeDe = (langue) => LOCALES[langue] || LOCALES.fr;

// ─────────────────────────────────────────────────────────────────────────────
// Fonctions PURES. Exportées pour être testables hors React ; les écrans, eux,
// passent par le hook, et une garde statique vérifie qu'aucun ne les importe
// directement — sinon la langue de l'application cesserait d'être suivie.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un INSTANT → date + heure, dans le fuseau du lecteur.
 *
 * L'entrée est l'ISO 8601 suffixé `Z` que sert le serveur depuis le lot T2.
 * Sans ce `Z`, le navigateur lirait la chaîne comme une heure LOCALE et
 * retrancherait l'offset du lecteur à une valeur déjà juste — c'est pourquoi
 * T2 devait précéder T3, et pas l'inverse.
 */
export const formaterHorodatage = (iso, langue) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(localeDe(langue), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Un INSTANT → son JOUR chez le lecteur.  🔴 CONVERTIT — voir l'en-tête.
 *
 * À ne pas confondre avec `formaterDateSaisie`, qui rend la même forme et ne
 * convertit jamais : un événement à 23:30 UTC le 11 est le **12** à Jérusalem.
 */
export const formaterHorodatageEnJour = (iso, langue) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(localeDe(langue), {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

/**
 * Un JOUR CIVIL → le même jour, partout.  🔴 NE CONVERTIT JAMAIS.
 *
 * ⚠️ ON NE PARSE PAS LA CHAÎNE EN `Date`. `new Date("2026-08-11")` est parsé
 * comme minuit **UTC** (ECMA-262) et dérape d'un jour à l'ouest de Greenwich ;
 * `new Date("2026-08-11T00:00:00")` est parsé comme minuit **local** et dérape
 * à l'est. Aucune des deux formes n'est sûre. On construit donc **par
 * composantes** — `new Date(a, m - 1, j)` —, seule forme mesurée juste sous
 * Europe/Paris, Asia/Jerusalem, America/New_York et Pacific/Honolulu.
 *
 * Tolère une valeur qui porterait encore une heure (`"…T23:30:00Z"`) : la
 * partie heure est coupée AVANT toute construction, jamais convertie.
 */
export const formaterDateSaisie = (jour, langue) => {
  if (!jour) return '';
  const [a, m, j] = String(jour).split('T')[0].split('-').map(Number);
  if (!a || !m || !j) return String(jour);
  return new Date(a, m - 1, j).toLocaleDateString(localeDe(langue), {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

/**
 * Aujourd'hui CHEZ LE LECTEUR, au format `"AAAA-MM-JJ"` qu'attend un
 * `<input type="date">` et que le serveur lit comme un jour civil.
 *
 * 🔴 CORRIGE UNE DONNÉE MÉTIER FAUSSE, PAS UN ARRONDI D'AFFICHAGE. Les
 * formulaires se pré-remplissaient avec `new Date().toISOString().split('T')[0]`
 * — c'est-à-dire **aujourd'hui en UTC**. Mesuré sous `Asia/Jerusalem` : à
 * **01:00 le 12 août, la valeur proposée était `"2026-08-11"`**. Entre minuit
 * et 03:00, un client israélien saisissait donc ses commandes **à la date de la
 * VEILLE**, et cette date partait en base puis sur le bon de commande envoyé au
 * fournisseur.
 *
 * ⚠️ `toISOString()` est le piège : il rend TOUJOURS de l'UTC. Pour un jour
 * civil local, il faut lire les composantes locales, jamais l'ISO.
 *
 * ⚠️ SEULE FONCTION DU MODULE QUE LES ÉCRANS IMPORTENT DIRECTEMENT, et la
 * raison est qu'elle **ne dépend d'aucune langue** : elle rend une valeur de
 * formulaire, pas un texte. La faire transiter par le hook obligerait à
 * respecter l'ordre des Hooks dans des initialiseurs `useState(...)`, pour
 * n'ajouter aucune information. Les trois autres, elles, doivent passer par le
 * hook — la garde statique le vérifie.
 */
export const jourCivilAujourdhui = () => {
  const maintenant = new Date();
  const deuxChiffres = (n) => String(n).padStart(2, '0');
  return `${maintenant.getFullYear()}-${deuxChiffres(maintenant.getMonth() + 1)}`
       + `-${deuxChiffres(maintenant.getDate())}`;
};

/** Au-delà de ce délai, un horodatage relatif cesse d'informer et redevient
 *  une date. Vingt-quatre heures : « il y a 30 h » demande un calcul mental que
 *  « hier 15:38 » ne demande pas. */
export const SEUIL_RELATIF_MS = 24 * 60 * 60 * 1000;

/**
 * Un INSTANT → « il y a 12 min » près de maintenant, une date au-delà.
 *
 * 🔴 UNE SEULE ÉCRITURE POUR LE PANNEAU ET POUR LA PAGE. Deux écritures du
 * « il y a N minutes » seraient la divergence de trop — on en a fermé six, du
 * vocabulaire de règlement à la quantité de réapprovisionnement. Le panneau
 * montre du récent, la page montre tout : **c'est le même besoin avec deux
 * plages, pas deux besoins.**
 *
 * ⚠️ LE RELATIF SE CALCULE DANS LE FUSEAU DU LECTEUR, ET LA CONVERSION N'EST PAS
 * RÉÉCRITE : un écart entre deux INSTANTS ne dépend d'aucun fuseau — c'est une
 * durée. C'est le basculement vers l'absolu qui en dépend, et il délègue à
 * `formaterHorodatage`, seul détenteur de la conversion.
 *
 * ⚠️ ET `maintenant` EST UN PARAMÈTRE, pas un `new Date()` caché : une fonction
 * qui lit l'horloge à l'intérieur n'est pas vérifiable — c'est la leçon de
 * `models/horloge.py`, appliquée côté client.
 */
export const formaterHorodatageRelatif = (iso, langue, maintenant = new Date()) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);

  const ecart = maintenant.getTime() - d.getTime();
  // ⚠️ UN INSTANT DANS LE FUTUR RETOMBE SUR L'ABSOLU. Une horloge client en
  // avance sur le serveur rendrait « il y a −2 min », qui n'est pas une durée.
  if (ecart < 0 || ecart >= SEUIL_RELATIF_MS) return formaterHorodatage(iso, langue);

  const minutes = Math.floor(ecart / 60000);
  if (minutes < 1) return TRADUCTIONS_RELATIF[localeDe(langue)].instant;
  if (minutes < 60) return TRADUCTIONS_RELATIF[localeDe(langue)].minutes(minutes);
  return TRADUCTIONS_RELATIF[localeDe(langue)].heures(Math.floor(minutes / 60));
};

// ⚠️ LES TROIS FORMES VIVENT ICI ET NON DANS LES LOCALES, et c'est délibéré :
// `Intl.RelativeTimeFormat` les rend déjà dans les trois langues, mais l'appeler
// pour trois cas connus ajouterait une dépendance de formatage là où trois
// gabarits suffisent. *Ce module est le seul endroit qui met en forme une date ;
// le sortir d'ici pour le mettre dans un fichier de traduction rouvrirait la
// porte que le lot T3 a fermée.*
const TRADUCTIONS_RELATIF = {
  'fr-FR': { instant: "à l'instant",
             minutes: (n) => `il y a ${n} min`,
             heures: (n) => `il y a ${n} h` },
  'en-US': { instant: 'just now',
             minutes: (n) => `${n} min ago`,
             heures: (n) => `${n} h ago` },
  'he-IL': { instant: 'ממש עכשיו',
             minutes: (n) => `לפני ${n} דק׳`,
             heures: (n) => `לפני ${n} שע׳` },
};

/**
 * Un INSTANT → une étiquette d'axe courte, dans le fuseau du lecteur.
 * Rend `« lun. 24 »` en français, `« Mon 24 »` en anglais.
 *
 * 🔴 AJOUTÉE AU LOT RU6 POUR LE GRAPHIQUE DE LA SEMAINE, et elle vit ICI et
 * nulle part ailleurs. Le graphique groupe les mouvements **par jour du
 * lecteur** : la conversion instant → jour local est exactement ce que
 * `formaterHorodatageEnJour` fait déjà, et **elle n'est pas réécrite** — cette
 * fonction ne change que le FORMAT de l'étiquette, jamais la conversion.
 * ⚠️ *Une deuxième écriture de la conversion de fuseau serait la troisième
 * forme du même défaut* (lot T2, deux fois ; lot T3, quatorze sites).
 */
export const formaterJourCourt = (iso, langue) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(localeDe(langue), { weekday: 'short', day: '2-digit' });
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Le point d'entrée des écrans. Rend les quatre fonctions déjà liées à la
 * langue de l'application, pour que les appels restent lisibles :
 *
 *     const { formaterHorodatage, formaterDateSaisie } = useFormatageDates();
 *     …
 *     {formaterHorodatage(notification.created_at)}
 *     {formaterDateSaisie(order.order_date)}
 */
export const useFormatageDates = () => {
  const { language } = useLanguage();
  return useMemo(() => ({
    formaterHorodatage: (iso) => formaterHorodatage(iso, language),
    formaterHorodatageEnJour: (iso) => formaterHorodatageEnJour(iso, language),
    formaterDateSaisie: (jour) => formaterDateSaisie(jour, language),
    formaterJourCourt: (iso) => formaterJourCourt(iso, language),
    formaterHorodatageRelatif: (iso, maintenant) =>
      formaterHorodatageRelatif(iso, language, maintenant),
  }), [language]);
};
